<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import ExportColumnPicker from "@/components/ExportColumnPicker.vue";
import { toApiLanguage } from "@/utils/locale";
import {
  buildAoA,
  exportAoAToXlsx,
  exportDateSuffix,
  parseAmountLogDate,
} from "@/utils/excelExport";
import {
  EXPORT_TYPE_ORDERS,
  EXPORT_TYPE_USERS,
  EXPORT_TYPE_RECHARGE,
  EXPORT_TYPE_CONSUMPTION,
  EXPORT_TYPE_STATISTICS,
  getColumnsForType,
  filterColumnsByKeys,
  statisticsSummaryColumns,
  statisticsByPaymentColumns,
  statisticsByStatusColumns,
  statisticsTrendColumns,
} from "@/config/exportSchemas";

const LARGE_EXPORT_THRESHOLD = 5000;

export default {
  page() {
    return {
      title: this.title,
      meta: [{ name: "description" }],
    };
  },
  components: { Layout, PageHeader, ExportColumnPicker },
  data() {
    return {
      title: "",
      items: [],
      userinfo: null,
      loading: false,
      exportType: EXPORT_TYPE_ORDERS,
      selectedColumns: [],
      // orders
      orderTimes: [],
      selectedPaymentTypes: [-1],
      selectedOrderStatus: -1,
      orderUserType: 0,
      paymentTypes: [],
      orderStatus: [],
      // users
      userKeyword: "",
      // amount logs
      amountUserScope: "selected", // selected | all
      selectedUids: [],
      chargeUserOptions: [],
      chargeUserLoading: false,
      amountDateRange: [],
      searchTimer: null,
      // statistics
      statsTab: "daily",
      dailyDate: [],
      monthDate: [],
      pickerOptions: {},
      pickerOptionsDaily: {},
    };
  },
  computed: {
    typeOptions() {
      return [
        { value: EXPORT_TYPE_ORDERS, label: this.$t("export.types.orders") },
        { value: EXPORT_TYPE_USERS, label: this.$t("export.types.users") },
        { value: EXPORT_TYPE_RECHARGE, label: this.$t("export.types.recharge") },
        { value: EXPORT_TYPE_CONSUMPTION, label: this.$t("export.types.consumption") },
        { value: EXPORT_TYPE_STATISTICS, label: this.$t("export.types.statistics") },
      ];
    },
    currentColumns() {
      return getColumnsForType(this.exportType);
    },
    needsUser() {
      return (
        this.exportType === EXPORT_TYPE_RECHARGE ||
        this.exportType === EXPORT_TYPE_CONSUMPTION
      );
    },
    canExport() {
      if (!this.selectedColumns.length) return false;
      if (
        this.needsUser &&
        this.amountUserScope === "selected" &&
        !this.selectedUids.length
      ) {
        return false;
      }
      if (this.exportType === EXPORT_TYPE_ORDERS) {
        return Array.isArray(this.orderTimes) && this.orderTimes.length === 2;
      }
      if (this.exportType === EXPORT_TYPE_STATISTICS) {
        if (this.statsTab === "daily") {
          return Array.isArray(this.dailyDate) && this.dailyDate.length === 2;
        }
        if (this.statsTab === "month") {
          return Array.isArray(this.monthDate) && this.monthDate.length === 2;
        }
      }
      return true;
    },
  },
  mounted() {
    this.title = this.$t("menuitems.setting.export");
    this.items = [
      { text: this.$t("dashboard.text"), href: "/" },
      { text: this.$t("menuitems.setting.export"), active: true },
    ];
    try {
      this.userinfo = JSON.parse(localStorage.getItem("user"));
      if (!this.userinfo) {
        this.$message.error(this.$t("global.errors.loginrequired"));
        this.$router.push({ name: "login" });
        return;
      }
    } catch (e) {
      this.$message.error(this.$t("global.errors.loginrequired"));
      this.$router.push({ name: "login" });
      return;
    }
    this.initFilters();
    this.initDefaultRanges();
    const qType = this.$route.query.type;
    if (qType && getColumnsForType(qType).length) {
      this.exportType = qType;
    }
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    initFilters() {
      this.paymentTypes = [
        { value: -1, label: this.$t("device.orders.allstatus") },
        { value: 0, label: this.$t("device.orders.coinpay") },
        { value: 1, label: this.$t("device.orders.useramount") },
        { value: 2, label: this.$t("device.orders.alipay") },
        { value: 3, label: this.$t("device.orders.wxpay") },
        { value: 4, label: this.$t("device.orders.payment.applepay") },
        { value: 5, label: this.$t("device.orders.payment.googlewallet") },
        { value: 6, label: this.$t("device.orders.payment.amazonpay") },
        { value: 7, label: this.$t("device.orders.payment.qpay") },
        { value: 40, label: this.$t("device.orders.payment.bestpay") },
        { value: 41, label: this.$t("device.orders.payment.sandpay") },
        { value: 42, label: this.$t("device.orders.payment.paymentasia") },
        { value: 44, label: this.$t("device.orders.payment.cmb") },
        { value: 45, label: this.$t("device.orders.payment.icbc") },
        { value: 46, label: this.$t("device.orders.payment.allinpay") },
        { value: 55, label: this.$t("device.orders.payment.keruyun") },
        { value: 56, label: this.$t("device.orders.payment.youhao") },
        { value: 61, label: this.$t("device.orders.payment.nayax") },
        { value: 77, label: this.$t("device.orders.payment.coin") },
        { value: 82, label: this.$t("device.orders.payment.shouqianba") },
      ];
      this.orderStatus = [
        { value: -1, label: this.$t("device.orders.allstatus") },
        { value: 0, label: this.$t("device.orders.nopay") },
        { value: 1, label: this.$t("device.orders.orderpay") },
        { value: 3, label: this.$t("device.orders.refuntrue") },
      ];
      const that = this;
      this.pickerOptions = {
        shortcuts: [
          {
            text: that.$t("statistics.sales.month.recent3"),
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setMonth(start.getMonth() - 2);
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: that.$t("statistics.sales.month.recent6"),
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setMonth(start.getMonth() - 5);
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: that.$t("statistics.sales.month.recent12"),
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setMonth(start.getMonth() - 11);
              picker.$emit("pick", [start, end]);
            },
          },
        ],
      };
      this.pickerOptionsDaily = {
        shortcuts: [
          {
            text: that.$t("statistics.sales.daily.recent3"),
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 2);
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: that.$t("statistics.sales.daily.recent7"),
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 6);
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: that.$t("statistics.sales.daily.recent30"),
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setMonth(start.getMonth() - 1);
              picker.$emit("pick", [start, end]);
            },
          },
        ],
      };
    },
    initDefaultRanges() {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 6);
      const fmt = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
      };
      const fmtSlash = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}/${m}/${day}`;
      };
      this.dailyDate = [fmt(start), fmt(end)];
      this.orderTimes = [fmtSlash(start), fmtSlash(end)];
      const mEnd = new Date();
      const mStart = new Date();
      mStart.setMonth(mStart.getMonth() - 2);
      const fmtMonth = (d) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      this.monthDate = [fmtMonth(mStart), fmtMonth(mEnd)];
    },
    onTypeChange() {
      this.selectedColumns = [];
    },
    remoteSearchUsers(keyword) {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.fetchUsers(keyword);
      }, 300);
    },
    fetchUsers(keyword) {
      if (!this.userinfo) return;
      this.chargeUserLoading = true;
      this.$axios
        .post(
          this.apiuri,
          {
            action: "userlist",
            page: 1,
            limit: 50,
            keyword: (keyword || "").trim(),
          },
          { headers: { Openid: this.userinfo.openid } }
        )
        .then((response) => {
          this.chargeUserLoading = false;
          const next =
            response.data.status === 200 ? response.data.data || [] : [];
          // Keep already-selected users visible after remote search replaces options
          const map = {};
          (this.chargeUserOptions || []).forEach((u) => {
            if (this.selectedUids.includes(u.id)) map[u.id] = u;
          });
          next.forEach((u) => {
            map[u.id] = u;
          });
          this.chargeUserOptions = Object.keys(map).map((id) => map[id]);
        })
        .catch(() => {
          this.chargeUserLoading = false;
        });
    },
    async onExport() {
      if (!this.canExport) {
        if (
          this.needsUser &&
          this.amountUserScope === "selected" &&
          !this.selectedUids.length
        ) {
          this.$message.warning(this.$t("export.tips.select_user"));
        } else if (!this.selectedColumns.length) {
          this.$message.warning(this.$t("export.tips.select_columns"));
        } else {
          this.$message.warning(this.$t("export.tips.incomplete_filter"));
        }
        return;
      }
      try {
        this.loading = true;
        switch (this.exportType) {
          case EXPORT_TYPE_ORDERS:
            await this.exportOrders();
            break;
          case EXPORT_TYPE_USERS:
            await this.exportUsers();
            break;
          case EXPORT_TYPE_RECHARGE:
            await this.exportAmountLogs(1);
            break;
          case EXPORT_TYPE_CONSUMPTION:
            await this.exportAmountLogs(2);
            break;
          case EXPORT_TYPE_STATISTICS:
            await this.exportStatistics();
            break;
          default:
            break;
        }
      } catch (e) {
        console.error(e);
        this.$message.error(this.$t("global.errors.network"));
      } finally {
        this.loading = false;
      }
    },
    async confirmLargeExport(count) {
      if (count <= LARGE_EXPORT_THRESHOLD) return true;
      try {
        await this.$confirm(
          this.$t("export.tips.large_confirm", { n: count }),
          this.$t("export.tips.confirm_title"),
          { type: "warning" }
        );
        return true;
      } catch (e) {
        return false;
      }
    },
    writeSingleSheet(columns, rows, filenameBase) {
      const selected = filterColumnsByKeys(columns, this.selectedColumns);
      if (!selected.length) {
        this.$message.warning(this.$t("export.tips.select_columns"));
        return;
      }
      const aoa = buildAoA(selected, rows, (key) => this.$t(key));
      exportAoAToXlsx({
        sheets: [{ name: "Sheet1", aoa }],
        filename: `${filenameBase}_${exportDateSuffix()}.xlsx`,
      });
      this.$message.success(this.$t("export.tips.success"));
    },
    async exportOrders() {
      const date = this.orderTimes[0] + "," + this.orderTimes[1];
      const response = await this.$axios.post(
        this.apiuri,
        {
          action: "dump_orderlist_json",
          date: date,
          type: this.orderUserType,
          language: toApiLanguage(this.$i18n.locale),
          paycode: this.selectedPaymentTypes,
          status: this.selectedOrderStatus,
        },
        { headers: { Openid: this.userinfo.openid } }
      );
      const rows = response.data.table || [];
      if (!(await this.confirmLargeExport(rows.length))) return;
      if (!rows.length) {
        this.$message.warning(this.$t("export.tips.empty"));
        return;
      }
      this.writeSingleSheet(
        getColumnsForType(EXPORT_TYPE_ORDERS),
        rows,
        this.$t("export.types.orders")
      );
    },
    async fetchAllUsers(keywordOverride) {
      const keyword =
        keywordOverride !== undefined
          ? String(keywordOverride || "").trim()
          : (this.userKeyword || "").trim();
      const first = await this.$axios.post(
        this.apiuri,
        {
          action: "userlist",
          page: 1,
          limit: 100000,
          keyword,
          export: 1,
        },
        { headers: { Openid: this.userinfo.openid } }
      );
      if (first.data.status !== 200) {
        throw new Error(first.data.message || "load failed");
      }
      let rows = first.data.data || [];
      const total = parseInt(first.data.total, 10) || rows.length;
      const allpage = parseInt(first.data.allpage, 10) || 1;
      // If export:1 still paginated, pull remaining pages
      if (rows.length < total && allpage > 1) {
        const pageSize = rows.length || 50;
        const pages = Math.ceil(total / pageSize);
        for (let page = 2; page <= pages; page++) {
          const res = await this.$axios.post(
            this.apiuri,
            {
              action: "userlist",
              page,
              limit: pageSize,
              keyword,
              export: 1,
            },
            { headers: { Openid: this.userinfo.openid } }
          );
          if (res.data.status === 200) {
            rows = rows.concat(res.data.data || []);
          }
        }
      }
      return { rows, total: total || rows.length };
    },
    async resolveAmountLogUsers() {
      if (this.amountUserScope === "all") {
        const { rows } = await this.fetchAllUsers("");
        return rows;
      }
      const optionMap = {};
      (this.chargeUserOptions || []).forEach((u) => {
        optionMap[u.id] = u;
      });
      return (this.selectedUids || []).map((id) => {
        return optionMap[id] || { id, username: String(id) };
      });
    },
    async fetchUserChargeLogs(uid) {
      const response = await this.$axios.post(
        this.apiuri,
        {
          action: "userchargelogs",
          uid,
        },
        { headers: { Openid: this.userinfo.openid } }
      );
      return response.data.list || [];
    },
    async exportUsers() {
      const { rows, total } = await this.fetchAllUsers();
      if (!(await this.confirmLargeExport(total))) return;
      if (!rows.length) {
        this.$message.warning(this.$t("export.tips.empty"));
        return;
      }
      this.writeSingleSheet(
        getColumnsForType(EXPORT_TYPE_USERS),
        rows,
        this.$t("export.types.users")
      );
    },
    filterAmountLogsByDate(list) {
      if (!Array.isArray(this.amountDateRange) || this.amountDateRange.length !== 2) {
        return list;
      }
      const start = new Date(this.amountDateRange[0] + "T00:00:00");
      const end = new Date(this.amountDateRange[1] + "T23:59:59");
      return list.filter((row) => {
        const d = parseAmountLogDate(row.sn);
        if (!d) return false;
        return d >= start && d <= end;
      });
    },
    async exportAmountLogs(type) {
      const users = await this.resolveAmountLogUsers();
      if (!users.length) {
        this.$message.warning(this.$t("export.tips.empty_users"));
        return;
      }
      if (users.length > 1) {
        try {
          await this.$confirm(
            this.$t("export.tips.multi_user_confirm", { n: users.length }),
            this.$t("export.tips.confirm_title"),
            { type: "warning" }
          );
        } catch (e) {
          return;
        }
      }

      let list = [];
      const batchSize = 5;
      for (let i = 0; i < users.length; i += batchSize) {
        const batch = users.slice(i, i + batchSize);
        const parts = await Promise.all(
          batch.map(async (user) => {
            const logs = await this.fetchUserChargeLogs(user.id);
            return (logs || []).map((row) => ({
              ...row,
              _uid: user.id,
              _username: user.username || "",
              _phone: user.phone || "",
              _cardno: user.cardno || "",
            }));
          })
        );
        parts.forEach((part) => {
          list = list.concat(part);
        });
      }

      list = list.filter((row) => Number(row.type) === Number(type));
      list = this.filterAmountLogsByDate(list);
      if (!(await this.confirmLargeExport(list.length))) return;
      if (!list.length) {
        this.$message.warning(this.$t("export.tips.empty"));
        return;
      }
      const name =
        type === 1
          ? this.$t("export.types.recharge")
          : this.$t("export.types.consumption");
      this.writeSingleSheet(getColumnsForType(EXPORT_TYPE_RECHARGE), list, name);
    },
    paywayLabel(item) {
      const name = item.payway_name || "unknown";
      const key = `device.orders.payment.${name}`;
      return this.$te(key) ? this.$t(key) : name;
    },
    statusLabel(item) {
      const map = {
        paid: "device.orders.orderpay",
        unpaid: "device.orders.nopay",
        refund: "device.orders.refuntrue",
      };
      const key = map[item.status_name] || "device.orders.allstatus";
      return this.$t(key);
    },
    selectedKeysForSheet(sheet) {
      return this.selectedColumns
        .filter((k) => k.startsWith(sheet + "."))
        .map((k) => k.slice(sheet.length + 1));
    },
    async exportStatistics() {
      let postdata = { action: "querystatistics", type: this.statsTab };
      if (this.statsTab === "daily") {
        postdata.date = this.dailyDate;
      } else if (this.statsTab === "month") {
        postdata.date = this.monthDate;
      }
      const response = await this.$axios.post(this.apiuri, postdata, {
        headers: { Openid: this.userinfo.openid },
      });
      if (response.data.status != 200) {
        this.$message.error(response.data.msg || this.$t("global.errors.loaddata"));
        return;
      }
      const data = response.data.data || {};
      const summary = data.summary || {};
      const byPayment = data.by_payment || [];
      const byStatus = data.by_status || [];
      const trend = data.trend || [];

      const summaryRows = [
        {
          metricKey: "statistics.sales.total_orders",
          count: summary.total_count,
          amount: summary.paid_amount,
          volume: summary.paid_volume,
        },
        {
          metricKey: "statistics.sales.paid",
          count: summary.paid_count,
          amount: summary.paid_amount,
          volume: summary.paid_volume,
        },
        {
          metricKey: "statistics.sales.unpaid",
          count: summary.unpaid_count,
          amount: summary.unpaid_amount,
          volume: summary.unpaid_volume,
        },
        {
          metricKey: "statistics.sales.refund",
          count: summary.refund_count,
          amount: summary.refund_amount,
          volume: summary.refund_volume,
        },
      ];

      const paymentRows = byPayment.map((item) => ({
        ...item,
        paywayLabel: this.paywayLabel(item),
      }));
      const statusRows = byStatus.map((item) => ({
        ...item,
        statusLabel: this.statusLabel(item),
      }));
      const trendRows = [...trend].reverse();

      const sheets = [];
      const summaryCols = filterColumnsByKeys(
        statisticsSummaryColumns,
        this.selectedKeysForSheet("summary")
      );
      const t = (key) => this.$t(key);
      if (summaryCols.length) {
        sheets.push({
          name: "Summary",
          aoa: buildAoA(summaryCols, summaryRows, t),
        });
      }
      const paymentCols = filterColumnsByKeys(
        statisticsByPaymentColumns,
        this.selectedKeysForSheet("payment")
      );
      if (paymentCols.length) {
        sheets.push({
          name: "ByPayment",
          aoa: buildAoA(paymentCols, paymentRows, t),
        });
      }
      const statusCols = filterColumnsByKeys(
        statisticsByStatusColumns,
        this.selectedKeysForSheet("status")
      );
      if (statusCols.length) {
        sheets.push({
          name: "ByStatus",
          aoa: buildAoA(statusCols, statusRows, t),
        });
      }
      if (this.statsTab !== "all") {
        const trendCols = filterColumnsByKeys(
          statisticsTrendColumns,
          this.selectedKeysForSheet("trend")
        );
        if (trendCols.length) {
          sheets.push({
            name: "Trend",
            aoa: buildAoA(trendCols, trendRows, t),
          });
        }
      }
      if (!sheets.length) {
        this.$message.warning(this.$t("export.tips.select_columns"));
        return;
      }
      exportAoAToXlsx({
        sheets,
        filename: `${this.$t("export.types.statistics")}_${this.statsTab}_${exportDateSuffix()}.xlsx`,
      });
      this.$message.success(this.$t("export.tips.success"));
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col-12">
        <div class="card" v-loading="loading">
          <div class="card-body">
            <el-form label-width="120px" label-position="left">
              <el-form-item :label="$t('export.form.data_type')">
                <el-radio-group v-model="exportType" @change="onTypeChange">
                  <el-radio-button
                    v-for="opt in typeOptions"
                    :key="opt.value"
                    :label="opt.value"
                  >
                    {{ opt.label }}
                  </el-radio-button>
                </el-radio-group>
              </el-form-item>

              <!-- Orders filters -->
              <template v-if="exportType === 'orders'">
                <el-form-item :label="$t('device.orders.ordertime')">
                  <el-date-picker
                    v-model="orderTimes"
                    type="daterange"
                    align="right"
                    unlink-panels
                    value-format="yyyy/MM/dd"
                    format="yyyy/MM/dd"
                    :range-separator="$t('statistics.sales.month.to')"
                    :start-placeholder="$t('statistics.sales.daily.start')"
                    :end-placeholder="$t('statistics.sales.daily.end')"
                  />
                </el-form-item>
                <el-form-item :label="$t('device.orders.paytype')">
                  <el-select
                    v-model="selectedPaymentTypes"
                    multiple
                    collapse-tags
                    style="width: 360px"
                  >
                    <el-option
                      v-for="item in paymentTypes"
                      :key="'pay-' + item.value"
                      :label="item.label"
                      :value="item.value"
                    />
                  </el-select>
                </el-form-item>
                <el-form-item :label="$t('device.orders.orderstatus')">
                  <el-select v-model="selectedOrderStatus" style="width: 240px">
                    <el-option
                      v-for="item in orderStatus"
                      :key="'st-' + item.value"
                      :label="item.label"
                      :value="item.value"
                    />
                  </el-select>
                </el-form-item>
                <el-form-item :label="$t('export.form.order_user_type')">
                  <el-radio-group v-model="orderUserType">
                    <el-radio :label="0">{{ $t("export.form.order_all") }}</el-radio>
                    <el-radio :label="1">{{ $t("device.lists.excelreg") }}</el-radio>
                    <el-radio :label="2">{{ $t("device.lists.excelnonereg") }}</el-radio>
                  </el-radio-group>
                </el-form-item>
              </template>

              <!-- Users filters -->
              <template v-if="exportType === 'users'">
                <el-form-item :label="$t('user.list.search')">
                  <el-input
                    v-model="userKeyword"
                    clearable
                    style="width: 320px"
                    :placeholder="$t('user.list.search')"
                  />
                </el-form-item>
              </template>

              <!-- Recharge / Consumption -->
              <template v-if="needsUser">
                <el-alert
                  class="mb-3"
                  type="info"
                  :closable="false"
                  :title="$t('export.tips.amount_user_hint')"
                />
                <el-form-item :label="$t('export.form.user_scope')">
                  <el-radio-group v-model="amountUserScope">
                    <el-radio label="selected">
                      {{ $t("export.form.user_scope_selected") }}
                    </el-radio>
                    <el-radio label="all">
                      {{ $t("export.form.user_scope_all") }}
                    </el-radio>
                  </el-radio-group>
                </el-form-item>
                <el-form-item
                  v-if="amountUserScope === 'selected'"
                  :label="$t('export.form.user')"
                  required
                >
                  <el-select
                    v-model="selectedUids"
                    multiple
                    filterable
                    remote
                    clearable
                    collapse-tags
                    :remote-method="remoteSearchUsers"
                    :loading="chargeUserLoading"
                    style="width: 420px"
                    :placeholder="$t('export.form.user_multi_placeholder')"
                    @focus="fetchUsers('')"
                  >
                    <el-option
                      v-for="u in chargeUserOptions"
                      :key="u.id"
                      :label="(u.username || '') + (u.phone ? ' / ' + u.phone : '')"
                      :value="u.id"
                    />
                  </el-select>
                </el-form-item>
                <el-form-item :label="$t('export.form.date_optional')">
                  <el-date-picker
                    v-model="amountDateRange"
                    type="daterange"
                    unlink-panels
                    value-format="yyyy-MM-dd"
                    format="yyyy-MM-dd"
                    :range-separator="$t('statistics.sales.month.to')"
                    :start-placeholder="$t('statistics.sales.daily.start')"
                    :end-placeholder="$t('statistics.sales.daily.end')"
                  />
                </el-form-item>
              </template>

              <!-- Statistics -->
              <template v-if="exportType === 'statistics'">
                <el-form-item :label="$t('export.form.stats_period')">
                  <el-radio-group v-model="statsTab">
                    <el-radio-button label="daily">
                      {{ $t("statistics.sales.daily.text") }}
                    </el-radio-button>
                    <el-radio-button label="month">
                      {{ $t("statistics.sales.month.text") }}
                    </el-radio-button>
                    <el-radio-button label="all">
                      {{ $t("statistics.sales.all") }}
                    </el-radio-button>
                  </el-radio-group>
                </el-form-item>
                <el-form-item
                  v-if="statsTab === 'daily'"
                  :label="$t('statistics.sales.daily.v2')"
                >
                  <el-date-picker
                    v-model="dailyDate"
                    type="daterange"
                    unlink-panels
                    value-format="yyyy-MM-dd"
                    format="yyyy-MM-dd"
                    :range-separator="$t('statistics.sales.month.to')"
                    :start-placeholder="$t('statistics.sales.daily.start')"
                    :end-placeholder="$t('statistics.sales.daily.end')"
                    :picker-options="pickerOptionsDaily"
                  />
                </el-form-item>
                <el-form-item
                  v-if="statsTab === 'month'"
                  :label="$t('statistics.sales.month.v2')"
                >
                  <el-date-picker
                    v-model="monthDate"
                    type="monthrange"
                    unlink-panels
                    value-format="yyyy-MM"
                    :range-separator="$t('statistics.sales.month.to')"
                    :start-placeholder="$t('statistics.sales.month.start')"
                    :end-placeholder="$t('statistics.sales.month.end')"
                    :picker-options="pickerOptions"
                  />
                </el-form-item>
              </template>

              <el-form-item :label="$t('export.form.columns')">
                <ExportColumnPicker
                  :type-key="exportType"
                  :columns="currentColumns"
                  v-model="selectedColumns"
                />
              </el-form-item>

              <el-form-item>
                <el-button
                  type="primary"
                  icon="el-icon-download"
                  :disabled="!canExport"
                  :loading="loading"
                  @click="onExport"
                >
                  {{ $t("export.form.export_btn") }}
                </el-button>
              </el-form-item>
            </el-form>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
